import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getDocument, retryAnalysis, exportPdfUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, FileText, ArrowLeft, AlertCircle, RotateCw, CheckCircle2, Plus,
  Download, Bell, ShieldAlert, ListTodo, HardHat, Clipboard, Calendar, Lock, Info,
  Scan, Telescope,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import ReminderPanel from "@/components/ReminderPanel";
import KpiBanner from "@/components/KpiBanner";
import { useAuth, ROLES } from "@/contexts/AuthContext";

const POLL_MS = 2500;

// -----------------------------------------------------------------------------
// Loading + error states (unchanged)
// -----------------------------------------------------------------------------
const stageMeta = {
  reading:             { label: "Lettura documento...",        from: 0,   to: 10  },
  analyzing:           { label: "Analisi in corso...",         from: 10,  to: 90  },
  analyzing_structure: { label: "Analisi struttura contratto...", from: 0,   to: 50  },
  analyzing_risks:     { label: "Analisi rischi e azioni...",     from: 50,  to: 90  },
  aggregating:         { label: "Preparazione risultati...",      from: 90,  to: 100 },
  done:                { label: "Completato",                     from: 100, to: 100 },
};
function computePct(progress) {
  if (!progress) return 5;
  const meta = stageMeta[progress.stage] || stageMeta.reading;
  if (progress.stage === "analyzing" && progress.total > 0) {
    const ratio = (progress.completed || 0) / progress.total;
    return Math.round(meta.from + (meta.to - meta.from) * ratio);
  }
  return meta.from;
}
const Analyzing = ({ filename, progress }) => {
  const meta = stageMeta[progress?.stage] || stageMeta.reading;
  const pct = computePct(progress);
  return (
    <div data-testid="analysis-loading" className="max-w-2xl mx-auto px-4 py-16 sm:py-24">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-slate-700 animate-spin" />
        </div>
        <h1 className="mt-6 text-2xl sm:text-3xl font-bold tracking-tight" data-testid="analyzing-title">
          {meta.label}
        </h1>
        <p className="mt-2 text-slate-600 text-sm">{filename}</p>
      </div>
      <div className="mt-10" data-testid="progress-bar">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-2 font-medium">
          <span>{progress?.stage === "analyzing" && progress.total > 0
            ? `${progress.completed} di ${progress.total} sezioni`
            : meta.label}</span>
          <span data-testid="progress-pct">{pct}%</span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-slate-900 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};
const ErrorState = ({ error, onRetry, canRetry }) => (
  <div data-testid="analysis-error" className="max-w-xl mx-auto text-center px-4 py-20">
    <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center">
      <AlertCircle className="w-5 h-5 text-red-600" />
    </div>
    <h1 className="mt-6 text-2xl font-bold">Qualcosa non ha funzionato</h1>
    <p className="mt-2 text-slate-600 break-words">{error || "Analisi fallita"}</p>
    {canRetry && (
      <button
        data-testid="retry-btn"
        onClick={onRetry}
        className="mt-6 inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-5 py-2.5 rounded-md hover:bg-slate-800 text-sm"
      >
        <RotateCw size={16} /> Riprova analisi
      </button>
    )}
  </div>
);

// -----------------------------------------------------------------------------
// Extraction mode banner — tells the user how the doc was processed.
// -----------------------------------------------------------------------------
const ExtractionModeBanner = ({ mode, wordCount }) => {
  if (mode === "ocr") {
    return (
      <div
        data-testid="extraction-banner-ocr"
        className="mb-6 flex items-start gap-2 text-xs text-sky-900 bg-sky-50 border border-sky-300 rounded-md px-3 py-2"
      >
        <Scan size={14} className="mt-0.5 shrink-0" />
        <span>
          <b>Documento elaborato tramite OCR</b>{wordCount ? ` (${wordCount.toLocaleString("it-IT")} parole)` : ""} —
          il PDF era una scansione/immagine. Il testo è stato riconosciuto automaticamente in italiano.
          Verifica con attenzione gli articoli critici (importi, percentuali, date) perché l'OCR può introdurre piccoli errori di trascrizione.
        </span>
      </div>
    );
  }
  if (!mode || mode === "full") {
    return (
      <div
        data-testid="extraction-banner-full"
        className="mb-6 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"
      >
        <Scan size={14} />
        <span>Analisi completa del documento{wordCount ? ` (${wordCount.toLocaleString("it-IT")} parole)` : ""}.</span>
      </div>
    );
  }
  if (mode === "focused") {
    return (
      <div
        data-testid="extraction-banner-focused"
        className="mb-6 flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2"
      >
        <Telescope size={14} className="mt-0.5 shrink-0" />
        <span>
          <b>Documento lungo</b>{wordCount ? ` (${wordCount.toLocaleString("it-IT")} parole)` : ""} — analisi focalizzata sulle sezioni critiche
          (intestazione, pagamenti, penali, sicurezza, garanzie, clausole finali).
          Le sezioni intermedie meno rilevanti sono state escluse per garantire un'analisi completa entro i limiti del modello.
        </span>
      </div>
    );
  }
  if (mode === "simplified") {
    return (
      <div
        data-testid="extraction-banner-simplified"
        className="mb-6 flex items-start gap-2 text-xs text-orange-900 bg-orange-50 border border-orange-300 rounded-md px-3 py-2"
      >
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <span>
          <b>Analisi semplificata</b> — sono mostrati solo Sommario, Rischi HIGH e Documenti richiesti.
          Per l'analisi completa di pagamenti, scadenze e piano d'azione esteso, riprova con "Riesegui analisi".
        </span>
      </div>
    );
  }
  return null;
};

// -----------------------------------------------------------------------------
// Shared building blocks
// -----------------------------------------------------------------------------
const InfoBox = ({ label, value, testid }) => (
  <div className="p-4 sm:p-5 border border-slate-200 rounded-lg bg-white" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <p className="mt-2 text-base sm:text-lg font-bold text-slate-900 break-words leading-snug">{value || "—"}</p>
  </div>
);

const SEVERITY_ORDER = { HIGH: 0, MEDIUM: 1, LOW: 2 };
const sevStyle = {
  HIGH:   { bg: "bg-red-600",    text: "Alto" },
  MEDIUM: { bg: "bg-amber-500",  text: "Medio" },
  LOW:    { bg: "bg-emerald-600", text: "Basso" },
};

const RiskCard = ({ r, i }) => {
  const s = sevStyle[r.severita] || sevStyle.MEDIUM;
  return (
    <article
      data-testid={`risk-card-${i}`}
      className="flex border border-slate-200 rounded-lg bg-white overflow-hidden"
    >
      <div className={`w-3 sm:w-4 ${s.bg} shrink-0`} aria-hidden />
      <div className="flex-1 p-4 sm:p-5 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className={`text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded ${s.bg}`}>
            {r.severita}
          </span>
          {r.clausola_riferimento && r.clausola_riferimento !== "Non specificato" && (
            <span className="text-[10px] font-mono text-slate-500">{r.clausola_riferimento}</span>
          )}
        </div>
        <h4 className="font-bold text-slate-900 text-base sm:text-lg leading-tight">{r.titolo}</h4>
        <p className="mt-2 text-sm text-slate-700 leading-relaxed">{r.descrizione}</p>
        {r.azione_consigliata && (
          <p className="mt-3 text-sm text-slate-900">
            <span className="font-semibold">Azione consigliata: </span>{r.azione_consigliata}
          </p>
        )}
        {r.impatto_economico && r.impatto_economico !== "Non quantificabile senza dati aggiuntivi" && (
          <p className="mt-1 text-xs text-slate-500">
            <span className="font-semibold">Impatto: </span>{r.impatto_economico}
          </p>
        )}
      </div>
    </article>
  );
};

const sortRischi = (rischi) =>
  [...(rischi || [])].sort((a, b) => (SEVERITY_ORDER[a.severita] ?? 9) - (SEVERITY_ORDER[b.severita] ?? 9));

// -----------------------------------------------------------------------------
// Generic tabs (Titolare + Direttore Lavori share these for read content)
// -----------------------------------------------------------------------------
const SommarioTab = ({ sommario }) => (
  <div data-testid="tab-sommario-content" className="space-y-6">
    <div className="grid sm:grid-cols-3 gap-3">
      <InfoBox label="Valore contratto" value={sommario.valore_contratto} testid="sommario-valore" />
      <InfoBox label="Durata" value={sommario.durata} testid="sommario-durata" />
      <InfoBox label="Tipo contratto" value={sommario.tipo_contratto} testid="sommario-tipo" />
    </div>
    <div className="p-5 sm:p-6 border border-slate-200 rounded-lg bg-white">
      <p className="text-slate-800 text-base leading-relaxed">{sommario.descrizione}</p>
    </div>
  </div>
);

const PagamentiTab = ({ pagamenti }) => {
  const sal = pagamenti.sal || [];
  const saldo = pagamenti.saldo_finale || {};
  return (
    <div data-testid="tab-pagamenti-content" className="space-y-4">
      {pagamenti.struttura && <p className="text-slate-700 text-sm leading-relaxed">{pagamenti.struttura}</p>}
      {sal.map((s, i) => (
        <div key={`sal-${s.numero}-${i}`} data-testid={`sal-card-${i}`} className="p-5 border border-slate-200 rounded-lg bg-white">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-900 text-white text-sm font-bold">{s.numero}</span>
            <span className="font-bold text-slate-900">{s.percentuale_avanzamento}</span>
            <span className="text-slate-300">·</span>
            <span className="font-mono text-sm text-slate-700">{s.importo}</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">Condizioni: </span>{s.condizioni}
          </p>
          {s.documenti_richiesti?.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Documenti richiesti</div>
              <div className="flex flex-wrap gap-1.5">
                {s.documenti_richiesti.map((d, j) => (
                  <span key={`${d}-${j}`} className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium">{d}</span>
                ))}
              </div>
            </div>
          )}
          {s.note && <p className="mt-3 text-xs text-slate-500 italic">{s.note}</p>}
        </div>
      ))}
      {(saldo.importo || saldo.condizioni) && (
        <div data-testid="saldo-finale-card" className="p-5 border-2 border-slate-900 rounded-lg bg-slate-50">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-900">Saldo finale</span>
            {saldo.importo && <span className="font-mono text-sm font-bold text-slate-900">{saldo.importo}</span>}
          </div>
          {saldo.condizioni && (
            <p className="text-sm text-slate-700 leading-relaxed">
              <span className="font-semibold">Condizioni: </span>{saldo.condizioni}
            </p>
          )}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3 pt-2">
        {pagamenti.termini_pagamento && <InfoBox label="Termini di pagamento" value={pagamenti.termini_pagamento} testid="termini-pagamento" />}
        {pagamenti.interessi_mora && <InfoBox label="Interessi di mora" value={pagamenti.interessi_mora} testid="interessi-mora" />}
      </div>
    </div>
  );
};

const DocumentiTab = ({ checklist, docId }) => {
  const storageKey = `contractai-checks-${docId}`;
  const [checked, setChecked] = useState({});
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) setChecked(JSON.parse(raw));
    } catch { /* noop */ }
  }, [storageKey]);
  const toggle = (key) => setChecked((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* noop */ }
    return next;
  });
  if (!checklist?.length) {
    return <p className="text-slate-500 text-sm" data-testid="tab-documenti-content">Nessun documento specifico richiesto dal contratto.</p>;
  }
  const totalChecked = Object.values(checked).filter(Boolean).length;
  return (
    <div data-testid="tab-documenti-content" className="space-y-3">
      <div className="flex items-center justify-between text-xs text-slate-500 px-1">
        <span>{checklist.length} documenti</span>
        <span data-testid="docs-checked-count">{totalChecked} / {checklist.length} spuntati</span>
      </div>
      <ul className="space-y-2">
        {checklist.map((d, i) => {
          const key = `${d.documento}-${i}`;
          const isChecked = !!checked[key];
          return (
            <li key={key} data-testid={`doc-item-${i}`}
              className={`flex items-start gap-3 p-4 border rounded-lg transition-colors ${
                isChecked ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white"
              }`}>
              <Checkbox checked={isChecked} onCheckedChange={() => toggle(key)} className="mt-0.5" data-testid={`doc-check-${i}`} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className={`font-bold text-slate-900 text-sm sm:text-base ${isChecked ? "line-through text-slate-500" : ""}`}>{d.documento}</span>
                  {d.quando && <span className="text-xs text-slate-500">— {d.quando}</span>}
                  {d.obbligatorio && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-900 text-white">Obbligatorio</span>}
                </div>
                {d.note && <p className="mt-1 text-xs text-slate-500">{d.note}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const RischiTab = ({ rischi }) => {
  const sorted = useMemo(() => sortRischi(rischi), [rischi]);
  if (!sorted.length) return <p className="text-slate-500 text-sm" data-testid="tab-rischi-content">Nessun rischio significativo rilevato.</p>;
  return (
    <div data-testid="tab-rischi-content" className="space-y-3">
      {sorted.map((r, i) => <RiskCard key={`${r.titolo}-${i}`} r={r} i={i} />)}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Piano d'azione (titolare): full action plan from AI, ordered by priority.
// Falls back to a client-side composition for older docs that don't have it.
// -----------------------------------------------------------------------------
const QUANDO_TONE = {
  "subito": "bg-red-600 text-white",
  "prima della firma": "bg-red-600 text-white",
  "prima dell'inizio lavori": "bg-amber-500 text-white",
  "prima di ogni sal": "bg-blue-700 text-white",
  "al collaudo": "bg-emerald-600 text-white",
};

const PianoAzioneTab = ({ piano, rischi, scadenze, checklist }) => {
  const items = useMemo(() => {
    if (Array.isArray(piano) && piano.length > 0) {
      return [...piano].sort((a, b) => (a.priorita ?? 99) - (b.priorita ?? 99));
    }
    // Legacy fallback (analyses produced before iter 14 don't have piano_di_azione)
    const out = [];
    sortRischi(rischi).forEach((r, i) => {
      if (r.azione_consigliata) {
        out.push({
          priorita: i + 1,
          azione: r.azione_consigliata,
          quando: "Prima della firma",
          perche: r.descrizione,
          collegato_a: r.titolo,
        });
      }
    });
    (scadenze || []).slice(0, 4).forEach((s, i) => {
      out.push({
        priorita: 100 + i,
        azione: s.descrizione || s.titolo,
        quando: s.data || "",
        perche: `Scadenza ${s.tipo}`,
        collegato_a: s.titolo,
      });
    });
    return out;
  }, [piano, rischi, scadenze, checklist]);

  if (!items.length) {
    return <p className="text-slate-500 text-sm" data-testid="tab-piano-content">Nessuna azione consigliata.</p>;
  }

  return (
    <div data-testid="tab-piano-content" className="space-y-3">
      <p className="text-xs text-slate-500 px-1">
        {items.length} azioni — ordinate per priorità (1 = massima urgenza).
      </p>
      {items.map((a, i) => {
        const whenKey = (a.quando || "").toLowerCase();
        const whenTone = QUANDO_TONE[whenKey] || "bg-slate-200 text-slate-800";
        return (
          <div key={`piano-${i}`} data-testid={`action-${i}`}
            className="flex gap-3 p-4 border border-slate-200 rounded-lg bg-white">
            <div className="w-10 h-10 rounded-md bg-brand-navy text-white flex items-center justify-center shrink-0 font-display font-black">
              {a.priorita ?? i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                {a.quando && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${whenTone}`}>
                    {a.quando}
                  </span>
                )}
                {a.collegato_a && a.collegato_a !== "Generale" && (
                  <span className="text-[10px] font-mono text-slate-400 truncate">↪ {a.collegato_a}</span>
                )}
              </div>
              <h4 className="font-bold text-sm sm:text-base leading-tight">{a.azione}</h4>
              {a.perche && (
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-800">Perché: </span>{a.perche}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Piano DL: only operational actions (SAL, avanzamento, scadenze)
// -----------------------------------------------------------------------------
const DL_KIND_KEYWORDS = ["sal", "avanzamento", "consegna", "collaudo", "fine lavori", "varianti", "cronoprogramma"];
const matchesDL = (text) => {
  const t = (text || "").toLowerCase();
  return DL_KIND_KEYWORDS.some((k) => t.includes(k));
};

const PianoDLTab = ({ rischi, scadenze }) => {
  const items = useMemo(() => {
    const out = [];
    sortRischi(rischi).forEach((r, i) => {
      const blob = `${r.titolo} ${r.descrizione} ${r.azione_consigliata}`;
      if (matchesDL(blob)) {
        out.push({
          id: `r-${i}`, kind: "Rischio operativo", sev: r.severita,
          title: r.titolo, text: r.azione_consigliata || r.descrizione,
          meta: r.clausola_riferimento, icon: ShieldAlert,
        });
      }
    });
    (scadenze || []).forEach((s, i) => {
      const tipo = (s.tipo || "").toLowerCase();
      if (["sal", "consegna", "collaudo"].includes(tipo) || matchesDL(s.titolo + " " + s.descrizione)) {
        out.push({
          id: `s-${i}`, kind: "Scadenza", sev: s.importanza,
          title: s.titolo, text: s.descrizione,
          meta: `${s.data || ""} · ${s.tipo || ""}`, icon: Calendar,
        });
      }
    });
    return out;
  }, [rischi, scadenze]);

  if (!items.length) {
    return (
      <div className="text-sm text-slate-600 bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="tab-piano-dl-content">
        Nessuna azione operativa (SAL / consegne / collaudi) ricavata da questo contratto.
      </div>
    );
  }
  return (
    <div data-testid="tab-piano-dl-content" className="space-y-3">
      <p className="text-xs text-slate-500 px-1">Vista filtrata: SAL, avanzamento, consegne, collaudi e scadenze operative.</p>
      {items.map((a, i) => {
        const s = sevStyle[a.sev] || sevStyle.MEDIUM;
        const Icon = a.icon;
        return (
          <div key={a.id} data-testid={`dl-action-${i}`}
            className="flex gap-3 p-4 border border-blue-200 rounded-lg bg-white">
            <div className={`w-9 h-9 rounded-md ${s.bg} text-white flex items-center justify-center shrink-0`}>
              <Icon size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-700 text-white">{a.kind}</span>
                {a.sev && <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white ${s.bg}`}>{a.sev}</span>}
                {a.meta && <span className="text-[10px] font-mono text-slate-400 truncate">{a.meta}</span>}
              </div>
              <h4 className="mt-1.5 font-bold text-sm sm:text-base">{a.title}</h4>
              <p className="mt-1 text-sm text-slate-700 leading-relaxed">{a.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Sicurezza tab (Responsabile Sicurezza)
// -----------------------------------------------------------------------------
const SAFETY_RISK_KEYWORDS = [
  "sicurezza", "d.lgs. 81", "dlgs 81", "dpi", "psc", "pos", "duvri",
  "infortun", "protezion", "formazione", "idoneità", "idoneita", "cantier",
  "cse", "csp", "stupefacenti", "alcol", "subappaltator",
];
const SAFETY_DOC_KEYWORDS = [
  "psc", "pos", "duvri", "idoneit", "tessera di riconoscimento",
  "tessera riconoscimento", "formazione", "sorveglianza sanitaria",
  "rspp", "dpi", "verbale di consegna", "primo soccorso",
  "antincendio", "rls",
];
const containsAny = (text, kws) => {
  const t = (text || "").toLowerCase();
  return kws.some((k) => t.includes(k));
};

const SicurezzaTab = ({ rischi, scadenze, checklist }) => {
  const safetyRischi = useMemo(() =>
    sortRischi(rischi.filter((r) =>
      (r.severita === "HIGH" || r.severita === "MEDIUM") &&
      containsAny(`${r.titolo} ${r.descrizione} ${r.clausola_riferimento}`, SAFETY_RISK_KEYWORDS)
    )), [rischi]);

  const safetyDocs = useMemo(() =>
    (checklist || []).filter((c) => containsAny(`${c.documento} ${c.note}`, SAFETY_DOC_KEYWORDS)),
    [checklist]
  );

  const safetyScadenze = useMemo(() =>
    (scadenze || []).filter((s) => {
      const t = (s.tipo || "").toLowerCase();
      return t === "durc" || containsAny(`${s.titolo} ${s.descrizione}`, SAFETY_RISK_KEYWORDS);
    }), [scadenze]
  );

  return (
    <div data-testid="tab-sicurezza-content" className="space-y-8">
      <section data-testid="sicurezza-rischi">
        <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
          <ShieldAlert size={18} className="text-orange-500" /> Rischi sicurezza ({safetyRischi.length})
        </h2>
        {safetyRischi.length === 0 ? (
          <p className="text-sm text-slate-500">Nessun rischio sicurezza rilevato nel contratto.</p>
        ) : (
          <div className="space-y-3">{safetyRischi.map((r, i) => <RiskCard key={`sr-${i}`} r={r} i={i} />)}</div>
        )}
      </section>

      <section data-testid="sicurezza-checklist">
        <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
          <Clipboard size={18} className="text-orange-500" /> Documenti di sicurezza ({safetyDocs.length})
        </h2>
        {safetyDocs.length === 0 ? (
          <p className="text-sm text-slate-500">Nessun documento di sicurezza specifico nella checklist contrattuale. Verifica comunque: PSC, POS, DUVRI, idoneità tecnico-professionale, tessera di riconoscimento.</p>
        ) : (
          <ul className="space-y-2">
            {safetyDocs.map((c, i) => (
              <li key={`sd-${i}`} className="p-4 border border-orange-200 bg-orange-50/40 rounded-lg">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-bold text-slate-900">{c.documento}</span>
                  {c.quando && <span className="text-xs text-slate-600">— {c.quando}</span>}
                  {c.obbligatorio && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-500 text-white">Obbligatorio</span>}
                </div>
                {c.note && <p className="mt-1 text-xs text-slate-600">{c.note}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section data-testid="sicurezza-scadenze">
        <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
          <Calendar size={18} className="text-orange-500" /> Scadenze e obblighi formativi ({safetyScadenze.length})
        </h2>
        {safetyScadenze.length === 0 ? (
          <p className="text-sm text-slate-500">Nessuna scadenza di sicurezza esplicita. Ricorda: DURC valido (120 giorni), formazione lavoratori (D.Lgs. 81/2008).</p>
        ) : (
          <ul className="space-y-2">
            {safetyScadenze.map((s, i) => {
              const sev = sevStyle[s.importanza] || sevStyle.MEDIUM;
              return (
                <li key={`ss-${i}`} className="p-4 border border-slate-200 bg-white rounded-lg flex items-start gap-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shrink-0 ${sev.bg}`}>
                    {s.importanza}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{s.titolo}</p>
                    <p className="text-xs text-slate-600 mt-0.5">{s.descrizione}</p>
                    <p className="text-[11px] font-mono text-slate-500 mt-1">{s.data} · {s.tipo}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Caposquadra: documenti lite + scadenze lite (no money)
// -----------------------------------------------------------------------------
const CAPO_OPERATIONAL_TIPI = new Set(["SAL", "Consegna", "Collaudo"]);

const DocumentiLiteTab = ({ checklist }) => {
  if (!checklist?.length) {
    return <p className="text-sm text-slate-500" data-testid="tab-documenti-lite-content">Nessun documento richiesto.</p>;
  }
  return (
    <div data-testid="tab-documenti-lite-content" className="space-y-3">
      <p className="text-xs text-slate-500 px-1">Documenti che tu o la tua squadra dovete consegnare.</p>
      <ul className="space-y-2">
        {checklist.map((c, i) => (
          <li key={`cl-${i}`} data-testid={`capo-doc-${i}`}
            className="p-4 border border-emerald-200 bg-white rounded-lg flex items-start gap-3">
            <span className="w-9 h-9 rounded-md bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <Clipboard size={15} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-slate-900">{c.documento}</p>
              {c.quando && <p className="text-xs text-slate-600 mt-0.5">{c.quando}</p>}
              {c.obbligatorio && (
                <span className="mt-1.5 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white">
                  Obbligatorio
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ScadenzeLiteTab = ({ scadenze }) => {
  const filtered = useMemo(() =>
    (scadenze || []).filter((s) => CAPO_OPERATIONAL_TIPI.has(s.tipo)),
    [scadenze]
  );
  if (!filtered.length) {
    return <p className="text-sm text-slate-500" data-testid="tab-scadenze-lite-content">Nessuna milestone operativa programmata.</p>;
  }
  return (
    <div data-testid="tab-scadenze-lite-content" className="space-y-3">
      <p className="text-xs text-slate-500 px-1">Milestone operative del cantiere — SAL, consegne, collaudi.</p>
      {filtered.map((s, i) => {
        const sev = sevStyle[s.importanza] || sevStyle.MEDIUM;
        return (
          <div key={`sl-${i}`} data-testid={`capo-scad-${i}`}
            className="p-4 border border-emerald-200 bg-white rounded-lg flex items-start gap-3">
            <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-white shrink-0 ${sev.bg}`}>
              {s.tipo}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">{s.titolo}</p>
              <p className="text-xs text-slate-600 mt-0.5">{s.descrizione}</p>
              <p className="text-[11px] font-mono text-slate-500 mt-1">{s.data}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Operaio: "I miei obblighi" — ultra-simple language
// -----------------------------------------------------------------------------
const OPERAIO_DOC_TEMPLATES = {
  "tessera di riconoscimento": "Devi portare sempre con te la tessera di riconoscimento.",
  "tessera riconoscimento": "Devi portare sempre con te la tessera di riconoscimento.",
  "dpi": "Devi indossare sempre i DPI (casco, scarpe antinfortunistiche, guanti, occhiali).",
  "casco": "Devi indossare il casco in tutta l'area del cantiere.",
  "scarpe antinfortunistiche": "Devi indossare le scarpe antinfortunistiche.",
  "imbracatura": "Devi indossare l'imbracatura quando lavori in quota.",
  "visita medica": "Devi avere la visita medica valida prima di entrare in cantiere.",
  "idoneità": "Devi avere l'idoneità sanitaria valida.",
  "idoneita": "Devi avere l'idoneità sanitaria valida.",
  "formazione": "Devi aver completato la formazione sulla sicurezza.",
  "patente": "Devi avere la patente del mezzo che usi (es. carrello elevatore, gru).",
  "pos": "Il tuo datore di lavoro deve aver consegnato il POS prima di iniziare i lavori.",
};

const simplifyDocument = (doc) => {
  const k = (doc.documento || "").toLowerCase();
  for (const key of Object.keys(OPERAIO_DOC_TEMPLATES)) {
    if (k.includes(key)) return OPERAIO_DOC_TEMPLATES[key];
  }
  return `Devi avere con te: ${doc.documento}.${doc.quando ? ` (${doc.quando})` : ""}`;
};

const MieiObblighiTab = ({ checklist, rischi }) => {
  const obblighi = useMemo(() => {
    const out = [];
    (checklist || []).forEach((c, i) => {
      const t = (c.documento || "").toLowerCase();
      const isOperaioRelevant = [
        "tessera", "dpi", "casco", "scarpe", "imbracatura", "visita",
        "idone", "formazione", "patente", "pos",
      ].some((k) => t.includes(k));
      if (isOperaioRelevant) {
        out.push({ id: `do-${i}`, text: simplifyDocument(c), icon: Clipboard });
      }
    });

    const safetyTitles = new Set();
    (rischi || []).forEach((r) => {
      const blob = `${r.titolo} ${r.descrizione}`.toLowerCase();
      if (containsAny(blob, ["dpi", "casco", "imbracatura", "formazione", "infortun"])) {
        safetyTitles.add(r.titolo);
      }
    });
    safetyTitles.forEach((title, idx) =>
      out.push({ id: `os-${idx}`, text: `Attenzione: ${title.toLowerCase()}.`, icon: ShieldAlert })
    );

    if (!out.length) {
      out.push(
        { id: "default-1", text: "Indossa sempre i DPI (casco, scarpe antinfortunistiche).", icon: HardHat },
        { id: "default-2", text: "Porta con te la tessera di riconoscimento.", icon: Clipboard },
        { id: "default-3", text: "Segui le istruzioni del tuo caposquadra.", icon: Info },
      );
    }
    return out;
  }, [checklist, rischi]);

  return (
    <div data-testid="tab-miei-obblighi-content" className="space-y-3">
      <p className="text-sm text-slate-600 px-1">Cosa devi fare e portare con te in cantiere.</p>
      {obblighi.map((o, i) => {
        const Icon = o.icon;
        return (
          <div key={o.id} data-testid={`obbligo-${i}`}
            className="flex items-start gap-3 p-4 border-2 border-slate-300 bg-white rounded-xl">
            <span className="w-10 h-10 rounded-lg bg-slate-700 text-white flex items-center justify-center shrink-0">
              <Icon size={18} />
            </span>
            <p className="font-medium text-base sm:text-lg text-slate-900 leading-snug pt-0.5">{o.text}</p>
          </div>
        );
      })}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Tab routing helper
// -----------------------------------------------------------------------------
const TAB_LABELS = {
  sommario: "Sommario",
  pagamenti: "Pagamenti",
  documenti: "Documenti",
  rischi: "Rischi",
  piano_azione: "Piano d'azione",
  piano_dl: "Piano DL",
  sicurezza: "Sicurezza",
  documenti_lite: "Documenti",
  scadenze_lite: "Scadenze",
  miei_obblighi: "I miei obblighi",
};

const TAB_ICONS = {
  piano_azione: ListTodo,
  piano_dl: ListTodo,
  sicurezza: ShieldAlert,
  documenti_lite: Clipboard,
  scadenze_lite: Calendar,
  miei_obblighi: HardHat,
};

// -----------------------------------------------------------------------------
// Main Analysis page
// -----------------------------------------------------------------------------
const Analysis = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { caps, effectiveRole, viewAsRole, actualRole } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchDoc = async () => {
      try {
        const d = await getDocument(id);
        if (cancelled) return;
        setDoc(d);
        setLoading(false);
        if (d.status === "pending" || d.status === "analyzing") {
          pollRef.current = setTimeout(fetchDoc, POLL_MS);
        }
      } catch {
        if (!cancelled) {
          toast.error("Documento non trovato");
          navigate("/");
        }
      }
    };
    fetchDoc();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [id, navigate]);

  const handleRetry = async (simple = false) => {
    try {
      await retryAnalysis(id, { simple });
      toast.success(simple ? "Analisi semplificata in corso" : "Analisi riavviata");
      const d = await getDocument(id);
      setDoc(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile riavviare");
    }
  };

  if (loading || !doc) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (doc.status === "pending" || doc.status === "analyzing") {
    return <Analyzing filename={doc.filename} progress={doc.progress} />;
  }
  if (doc.status === "error") {
    return (
      <ErrorState
        error={doc.error}
        onRetry={() => handleRetry(false)}
        onRetrySimple={() => handleRetry(true)}
        canRetry={!!caps?.canDelete}
      />
    );
  }

  const a = doc.analysis || {};
  const sommario = a.sommario || {};
  const pagamenti = a.pagamenti_sal || {};
  const checklist = (a.documenti_richiesti || {}).checklist || [];
  const rischi = a.rischi || [];
  const scadenze = a.scadenze || [];
  const piano = a.piano_di_azione || [];

  const tabIds = caps?.tabs || [];
  const defaultTab = tabIds[0] || "sommario";
  const currentTab = activeTab && tabIds.includes(activeTab) ? activeTab : defaultTab;
  const showDisclaimer = ["titolare", "direttore_lavori"].includes(effectiveRole);
  const canSeeReminders = caps?.canManageReminders;
  const canExport = ["titolare", "direttore_lavori"].includes(effectiveRole);

  // KPI banner shortcuts — resolve targets based on what this role can actually see.
  const risksTabId =
    tabIds.find((t) => t === "rischi") ||
    tabIds.find((t) => t === "sicurezza") ||
    null;
  const deadlinesTabId =
    tabIds.find((t) => t === "scadenze_lite") ||
    tabIds.find((t) => t === "pagamenti") ||
    null;
  const handleJumpToRisks = risksTabId ? () => setActiveTab(risksTabId) : undefined;
  const handleJumpToDeadlines = deadlinesTabId ? () => setActiveTab(deadlinesTabId) : undefined;
  const handleOpenReminders = canSeeReminders ? () => setRemindersOpen(true) : undefined;

  const roleColor = {
    titolare: "border-slate-900",
    direttore_lavori: "border-blue-700",
    responsabile_sicurezza: "border-orange-500",
    caposquadra: "border-emerald-600",
    operaio: "border-slate-500",
  }[effectiveRole] || "border-slate-300";

  return (
    <div data-testid="analysis-results" className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between mb-6">
        <Link to="/documenti" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-btn">
          <ArrowLeft size={14} /> Torna ai contratti
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {canSeeReminders && (
            <Dialog open={remindersOpen} onOpenChange={setRemindersOpen}>
              <DialogTrigger asChild>
                <button data-testid="open-reminders-btn"
                  className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-900 font-medium px-3 py-2 rounded-md hover:bg-slate-50 text-sm">
                  <Bell size={14} /> Promemoria
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Promemoria email</DialogTitle>
                  <DialogDescription>Ti avvisiamo 7, 3 e 1 giorno prima di ogni scadenza con data certa.</DialogDescription>
                </DialogHeader>
                <ReminderPanel documentId={doc.id} scadenze={scadenze} compact />
              </DialogContent>
            </Dialog>
          )}
          {canExport && (
            <a href={exportPdfUrl(doc.id)} target="_blank" rel="noopener noreferrer" data-testid="export-pdf-btn"
              className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-900 font-medium px-3 py-2 rounded-md hover:bg-slate-50 text-sm">
              <Download size={14} /> Scarica PDF
            </a>
          )}
          {caps?.canUpload && (
            <Link to="/" data-testid="new-analysis-btn"
              className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-3 py-2 rounded-md hover:bg-slate-800 text-sm">
              <Plus size={14} /> Nuovo
            </Link>
          )}
        </div>
      </div>

      {/* "Stai visualizzando come" banner for Titolare */}
      {actualRole === "titolare" && viewAsRole && (
        <div
          data-testid="viewing-as-banner"
          className="mb-5 rounded-md border border-dashed border-slate-400 bg-slate-50 text-slate-700 px-3 py-2 text-xs flex items-center gap-2"
        >
          <Lock size={13} className="text-slate-500" />
          Stai visualizzando come: <b>{ROLES[viewAsRole]?.label}</b>. Solo i contenuti utili a questo ruolo sono mostrati.
        </div>
      )}

      {/* Filename + role tag */}
      <div className={`flex items-center gap-3 mb-4 border-l-4 pl-3 ${roleColor}`}>
        <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
          <FileText size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">{doc.filename}</h1>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{doc.pages} pagine</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 size={12} /> Analisi completata
            </span>
            <span>·</span>
            <span className="font-semibold text-slate-700">Vista: {ROLES[effectiveRole]?.label}</span>
          </div>
        </div>
      </div>

      <ExtractionModeBanner mode={doc.extraction_mode} wordCount={doc.word_count} />

      {/* Sticky KPI banner — hidden for "operaio" whose UX is intentionally ultra-simple. */}
      {effectiveRole !== "operaio" && (
        <KpiBanner
          rischi={rischi}
          scadenze={scadenze}
          piano={piano}
          valoreContratto={
            ["titolare", "direttore_lavori"].includes(effectiveRole)
              ? sommario.valore_contratto
              : null
          }
          onJumpToRisks={handleJumpToRisks}
          onJumpToDeadlines={handleJumpToDeadlines}
          onOpenReminders={handleOpenReminders}
        />
      )}

      {/* Tabs */}
      {tabIds.length === 0 ? (
        <div className="p-6 border border-slate-200 rounded-lg bg-white text-slate-500 text-sm" data-testid="no-tabs">
          Il tuo ruolo non ha visualizzazioni assegnate.
        </div>
      ) : tabIds.length === 1 ? (
        <SingleTab tabId={tabIds[0]} doc={doc}
          sommario={sommario} pagamenti={pagamenti} checklist={checklist}
          rischi={rischi} scadenze={scadenze} piano={piano} />
      ) : (
        <Tabs value={currentTab} onValueChange={setActiveTab} className="w-full">
          <TabsList
            data-testid="tabs-list"
            className="w-full grid gap-1 bg-slate-100 p-1 rounded-md h-auto"
            style={{ gridTemplateColumns: `repeat(${tabIds.length}, minmax(0, 1fr))` }}
          >
            {tabIds.map((tId) => {
              const Icon = TAB_ICONS[tId];
              return (
                <TabsTrigger key={tId} value={tId} data-testid={`tab-${tId}`} className="text-xs sm:text-sm py-2 flex items-center justify-center gap-1.5">
                  {Icon && <Icon size={13} />}
                  {TAB_LABELS[tId]}
                </TabsTrigger>
              );
            })}
          </TabsList>
          <div className="mt-6">
            {tabIds.map((tId) => (
              <TabsContent key={tId} value={tId}>
                <SingleTab tabId={tId} doc={doc}
                  sommario={sommario} pagamenti={pagamenti} checklist={checklist}
                  rischi={rischi} scadenze={scadenze} piano={piano} />
              </TabsContent>
            ))}
          </div>
        </Tabs>
      )}

      {showDisclaimer && a.disclaimer && (
        <p data-testid="disclaimer" className="mt-12 text-xs text-slate-400 leading-relaxed text-center">
          {a.disclaimer}
        </p>
      )}
    </div>
  );
};

const SingleTab = ({ tabId, doc, sommario, pagamenti, checklist, rischi, scadenze, piano }) => {
  switch (tabId) {
    case "sommario":        return <SommarioTab sommario={sommario} />;
    case "pagamenti":       return <PagamentiTab pagamenti={pagamenti} />;
    case "documenti":       return <DocumentiTab checklist={checklist} docId={doc.id} />;
    case "rischi":          return <RischiTab rischi={rischi} />;
    case "piano_azione":    return <PianoAzioneTab piano={piano} rischi={rischi} scadenze={scadenze} checklist={checklist} />;
    case "piano_dl":        return <PianoDLTab rischi={rischi} scadenze={scadenze} />;
    case "sicurezza":       return <SicurezzaTab rischi={rischi} scadenze={scadenze} checklist={checklist} />;
    case "documenti_lite":  return <DocumentiLiteTab checklist={checklist} />;
    case "scadenze_lite":   return <ScadenzeLiteTab scadenze={scadenze} />;
    case "miei_obblighi":   return <MieiObblighiTab checklist={checklist} rischi={rischi} />;
    default:                return null;
  }
};

export default Analysis;
